import { Button, TypographyDetail } from '@seen/ui'
import { IconChevronDown } from '@tabler/icons-react'
import type { ReactNode } from 'react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '#src/components/core/dropdown-menu/index.ts'
import {
  INBOX_CATEGORIES,
  type InboxCategoryId,
} from '#src/hooks/inbox/inboxCategories.ts'
import {
  type InboxReadFilter,
  READ_FILTER_OPTIONS,
} from '#src/hooks/inbox/useInboxCategoryStore.ts'

interface InboxMobileFiltersProps {
  selectedCategory: InboxCategoryId
  onSelectCategory: (category: InboxCategoryId) => void
  counts: Record<InboxCategoryId, number>
  readFilter: InboxReadFilter
  onReadFilterChange: (filter: InboxReadFilter) => void
  isLoading?: boolean
}

export function InboxMobileFilters({
  selectedCategory,
  onSelectCategory,
  counts,
  readFilter,
  onReadFilterChange,
  isLoading = false,
}: InboxMobileFiltersProps): ReactNode {
  const selectedCategoryConfig = INBOX_CATEGORIES.find(
    (c) => c.id === selectedCategory
  )
  const selectedReadFilterLabel =
    READ_FILTER_OPTIONS.find((o) => o.value === readFilter)?.label ?? 'All'

  return (
    <div className="flex items-center justify-between py-1.5 pr-pagex-default pl-2">
      {/* Category dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="xs" className="items-center gap-1">
            <span className="font-medium text-muted-foreground">Category:</span>
            {selectedCategoryConfig?.label ?? 'All'}
            {isLoading ? (
              <span className="inline-block h-2.5 min-w-4 animate-pulse rounded bg-frame-foreground-secondary/30" />
            ) : (
              counts[selectedCategory] > 0 && (
                <TypographyDetail className="text-frame-foreground-secondary">
                  {counts[selectedCategory]}
                </TypographyDetail>
              )
            )}
            <IconChevronDown className="size-3 text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {INBOX_CATEGORIES.map((category) => {
            const count = counts[category.id]
            return (
              <DropdownMenuItem
                key={category.id}
                onSelect={() => onSelectCategory(category.id)}
                className="gap-2"
              >
                <span
                  className={`flex-1 ${category.id === selectedCategory ? 'font-semibold' : ''}`}
                >
                  {category.label}
                </span>
                {isLoading ? (
                  <span className="inline-block h-2.5 min-w-4 animate-pulse rounded bg-frame-foreground-secondary/30" />
                ) : (
                  count > 0 && (
                    <TypographyDetail className="text-frame-foreground-secondary">
                      {count}
                    </TypographyDetail>
                  )
                )}
              </DropdownMenuItem>
            )
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Read status dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="xs" className="items-center gap-1">
            <span className="font-medium text-muted-foreground">
              Read status:
            </span>
            {selectedReadFilterLabel}
            <IconChevronDown className="size-3 text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {READ_FILTER_OPTIONS.map((option) => (
            <DropdownMenuItem
              key={option.value}
              onSelect={() => onReadFilterChange(option.value)}
              className={option.value === readFilter ? 'font-semibold' : ''}
            >
              {option.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
